import React, { useState } from 'react';
import { MultiValue } from 'react-select';

import GenericSelect, { GenericSelectProps } from './GenericSelect';
import { MultiSelectOption } from './types';

/**
 * @name MadMultiSelect
 * MadMultiSelect is a mutliple choices select component based on GenericMadSelect features.
 * Specific features :
 *    - Handle fixed options
 */
function MadMultiSelect<
  MadOption extends MultiSelectOption,
  IsFree extends boolean = false
>(props: GenericSelectProps<MadOption, true, IsFree>) {
  const { value, onChange } = props;

  const [selectedOptions, setSelectedOptions] = useState<MultiValue<MadOption>>(
    value as MultiValue<MadOption>
  );

  return (
    <GenericSelect
      {...props}
      isMulti
      isClearable={selectedOptions.some(({ isFixed }) => !isFixed)}
      // The chip of the 2026 brand (see .mscore-chip): floral fill, linen
      // hairline, navy ink. Filling it with the theme's primary, as before, put
      // react-select's dark label on navy once primary became navy. A fixed
      // chip drops the fill rather than greying it, as .rimp-chip--idle does.
      styles={{
        multiValue: (base, state) => ({
          ...base,
          borderRadius: 5,
          border: state.data.isFixed
            ? '1px dashed var(--app-line)'
            : '1px solid var(--app-warm-line)',
          backgroundColor: state.data.isFixed
            ? 'transparent'
            : 'var(--app-warm)',
        }),
        multiValueLabel: (base, state) => ({
          ...base,
          color: state.data.isFixed ? 'var(--app-muted)' : 'var(--app-ink)',
          fontSize: 13,
        }),
        multiValueRemove: (base, state) =>
          state.data.isFixed
            ? { ...base, display: 'none' }
            : {
                ...base,
                color: 'var(--hg-navy-60)',
                ':hover': {
                  backgroundColor: 'var(--app-accent-soft)',
                  color: 'var(--app-ink)',
                },
              },
        ...props.styles,
      }}
      onChange={(newValues, actionMeta) => {
        let values = newValues;
        switch (actionMeta.action) {
          case 'remove-value':
          case 'pop-value':
            if (actionMeta.removedValue.isFixed) {
              return;
            }
            break;
          case 'clear':
            values = selectedOptions.filter(({ isFixed }) => isFixed);
            break;
        }

        values = [
          ...values.filter(({ isFixed }) => isFixed),
          ...values.filter(({ isFixed }) => !isFixed),
        ];

        setSelectedOptions(values);

        // forward onChange
        onChange(values, actionMeta);
      }}
    />
  );
}

export default MadMultiSelect;
